import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useTournamentMatchDetailQuery } from '@/services/endpoints/matches';
import { Loader } from '@/components/common/Loader';
import { StatusBadge } from '@/components/tournaments/StatusBadge';

/**
 * Match detail screen. Reached from the Tournament detail "Matches" tab.
 * The Laravel match endpoint requires both `tournamentId` and `matchId`,
 * so we expect the tournament id in the query string (`?t=<id>`) when the
 * link is built. Fallback: show a "Match data unavailable" placeholder.
 */
export function MatchDetail() {
  const { id = '' } = useParams<{ id: string }>();
  const [search] = useSearchParams();
  const tournamentParam = search.get('t');
  const { t } = useTranslation('tournaments');

  const matchId = Number(id);
  const tournamentId = Number(tournamentParam);
  const validIds =
    !!id && !!tournamentParam && !Number.isNaN(matchId) && !Number.isNaN(tournamentId);

  const { data: match, isLoading } = useTournamentMatchDetailQuery(
    { id: tournamentId, matchId },
    { skip: !validIds },
  );

  if (!validIds) {
    return (
      <div className="px-4 py-10 text-center">
        <p className="text-sm text-fg-muted">
          {t('match_context_missing', {
            defaultValue:
              'Open this match from a tournament — match links need the tournament context.',
          })}
        </p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader />
      </div>
    );
  }

  if (!match) {
    return (
      <div className="px-4 py-10 text-center">
        <p className="text-sm text-danger">{t('match_not_found', { defaultValue: 'Match not found.' })}</p>
      </div>
    );
  }

  const left = match.first_participant?.profile?.info?.nickname ?? 'TBD';
  const right = match.second_participant?.profile?.info?.nickname ?? 'TBD';

  return (
    <div className="flex flex-col gap-4 px-4 pb-6 pt-5">
      <header>
        <p className="text-xs font-medium uppercase tracking-wide text-fg-muted">
          {t('round', { defaultValue: 'Round' })} {match.round}
        </p>
        <h1 className="mt-1 text-2xl font-bold text-fg">
          {left}
          <span className="mx-2 text-fg-muted">vs</span>
          {right}
        </h1>
      </header>

      <section className="flex items-center justify-between rounded-xl bg-bg-secondary p-4">
        <div>
          <p className="text-xs uppercase tracking-wide text-fg-muted">
            {t('status', { defaultValue: 'Status' })}
          </p>
          <p className="mt-1 text-sm text-fg">{match.match_status}</p>
        </div>
        <StatusBadge status={match.match_status} />
      </section>

      <section className="rounded-xl bg-bg-secondary p-4">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-fg-muted">
          {t('result', { defaultValue: 'Result' })}
        </h2>
        <p className="mt-2 text-sm text-fg">
          {match.match_result ?? t('result_pending', { defaultValue: 'Result pending.' })}
        </p>
      </section>

      {match.chat_room && (
        <Link
          to={`/chat/${match.chat_room}`}
          className="flex items-center justify-center rounded-xl bg-brand py-3 text-sm font-semibold text-brand-fg transition-opacity active:opacity-80"
        >
          {t('open_match_chat', { defaultValue: 'Open match chat' })}
        </Link>
      )}
    </div>
  );
}
